import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { HtmlNode, WINDOW } from '@frontend/vanilla/core';
import { debounceTime, filter, take } from 'rxjs';

import { LoadingService } from '../common/loading.service';
import { PrintCallbackMessage, PrintCallbackType, PrintDeviceType } from './print.models';

@Component({
    template: '',
    standalone: false,
})
export abstract class PrintPageBaseComponent<TModel> extends OnRouteResolve<TModel> implements AfterViewInit {
    hasErrors?: boolean;
    private readonly _window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);
    private readonly loadingService = inject(LoadingService);

    printCommand: PrintDeviceType;

    constructor(
        route: ActivatedRoute,
        private htmlNodeService: HtmlNode,
        private timer: TimerService,
    ) {
        super(route);
        this.printCommand = (this.route.snapshot.queryParamMap?.get('device') as PrintDeviceType) || PrintDeviceType.Default;
    }

    ngAfterViewInit(): void {
        // Wait until dynamic content will be loaded
        this.loadingService.loading$
            .pipe(
                debounceTime(1000),
                filter((x) => !x),
                take(1),
            )
            .subscribe(() => this.print());
    }

    private print() {
        if (this.hasErrors) {
            this._window.parent.postMessage(<PrintCallbackMessage>{ type: PrintCallbackType.DataLoadingError }, '*');
            return;
        }

        this.htmlNodeService.setCssClass('card-content', true);
        if (this.printCommand === PrintDeviceType.Default) {
            this.triggerDefaultPrint();
            return;
        }

        //handling print in wrapperApp by triggering CCB event
        this._window.parent.postMessage(<PrintCallbackMessage>{ type: PrintCallbackType.PrintReadyForWrapper }, '*');
    }

    private triggerDefaultPrint(): void {
        this._window.onafterprint = () => {
            this._window.parent.postMessage(<PrintCallbackMessage>{ type: PrintCallbackType.PrintFinished }, '*');
        };

        this._window.parent.postMessage(<PrintCallbackMessage>{ type: PrintCallbackType.PrintStarted }, '*');
        const executePrint = () => {
            try {
                if (!this._doc.execCommand('print', false)) {
                    this.timer.setTimeout(() => this._window.print(), 2000);
                }
            } catch {
                this._window.print();
            }
        };

        executePrint();
    }
}
